import { BaseService } from './BaseService';
import { disponibilidadService } from './disponibilidadService';

/**
 * Servicio para gestionar notificaciones push locales
 */
class NotificationService extends BaseService {
  private readonly NOTIFICATION_PERMISSION_KEY = 'notification_permission';
  private readonly SCHEDULED_NOTIFICATIONS_KEY = 'scheduled_notifications';
  private readonly NOTIFICATIONS_ACTIVE_KEY = 'notifications_active';
  private readonly NOTIFICATIONS_USER_KEY = 'notifications_user_id';

  constructor() {
    super('/notifications');
  }

  /**
   * Solicita permisos de notificación al usuario
   */
  async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('Este navegador no soporta notificaciones');
      return false;
    }

    const permission = await Notification.requestPermission();
    localStorage.setItem(this.NOTIFICATION_PERMISSION_KEY, permission);
    return permission === 'granted';
  }

  /**
   * Verifica si los permisos están concedidos
   */
  hasPermission(): boolean {
    return Notification.permission === 'granted';
  }

  /**
   * Programa una notificación específica
   */
  /**
   * Programa una notificación específica con lógica corregida
   * Ahora maneja correctamente las notificaciones del día actual
   */
  private programarNotificacion(userId: number, dia: number, hora: number, minuto: number): void {
    const ahora = new Date();
    const proximaNotificacion = new Date();
    
    // Configurar la fecha objetivo
    proximaNotificacion.setHours(hora, minuto, 0, 0);
    
    // Calcular días hasta el próximo día objetivo
    let diasHastaProximo = (dia - ahora.getDay() + 7) % 7;
    
    // Lógica corregida para el día actual
    if (diasHastaProximo === 0) {
      // Es el mismo día, verificar si la hora ya pasó
      if (proximaNotificacion > ahora) {
        // La hora aún no ha pasado HOY, mantener para hoy
        diasHastaProximo = 0;
      } else {
        // La hora ya pasó HOY, programar para la próxima semana
        diasHastaProximo = 7;
      }
    } 
    
    // Establecer la fecha final
    proximaNotificacion.setDate(ahora.getDate() + diasHastaProximo);
    
    const tiempoHasta = proximaNotificacion.getTime() - ahora.getTime();
    console.log(`⏳ Tiempo hasta notificación: ${Math.round(tiempoHasta / (1000 * 60 * 60))} horas`);
    
    // Enviar al Service Worker en lugar de usar setTimeout local
    this.programarNotificacionConSW(userId, dia, hora, minuto, tiempoHasta);
  }

  /**
   * Envía mensaje al Service Worker para programar notificación
   */
  private enviarMensajeAServiceWorker(type: string, data: any): Promise<any> {
    return new Promise((resolve, reject) => {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type, data });
        resolve(true);
      } else {
        console.warn('⚠️ Service Worker no disponible');
        reject(new Error('Service Worker no disponible'));
      }
    });
  }

  /**
   * Programa una notificación específica usando Service Worker
   */
  private programarNotificacionConSW(
    userId: number,
    dia: number,
    hora: number,
    minuto: number,
    delay: number
  ): void {
    const id = `${userId}-${dia}-${hora}-${minuto}`;
    const titulo = '⚽ Recordatorio CorneApp';
    const mensaje = 'No olvides marcar tu disponibilidad para este fin de semana';
    
    this.enviarMensajeAServiceWorker('SCHEDULE_NOTIFICATION', {
      id,
      title: titulo,
      body: mensaje,
      delay,
      userId: userId.toString(),
      day: dia,
      hour: hora,
      minute: minuto
    }).catch(error => {
      console.error('❌ Error enviando al SW:', error);
      // Fallback al método anterior si el SW no está disponible
      this.programarNotificacionFallback(userId, dia, hora, minuto, delay);
    });
  }

  /**
   * Método fallback si el Service Worker no está disponible
   */
  private programarNotificacionFallback(
    userId: number,
    dia: number,
    hora: number,
    minuto: number,
    delay: number
  ): void {
    const timeoutId = setTimeout(async () => {
      await this.verificarYEnviarNotificacion(userId);
      // Reprogramar para la próxima semana
      this.programarNotificacion(userId, dia, hora, minuto);
    }, delay);
    
    this.guardarTimeoutId(timeoutId);
  }

  /**
   * Verifica si el entrenador tiene disponibilidades y envía notificación
   */
  private async verificarYEnviarNotificacion(userId: number): Promise<void> {
    try {
      const disponibilidades = await disponibilidadService.obtenerDisponibilidadesSemanaActual(userId);
            
      // Combinar disponibilidades de sábado y domingo
      const finDeSemana = [...disponibilidades.sabado, ...disponibilidades.domingo];
            
      if (finDeSemana.length === 0) {
        this.enviarNotificacion(
          '⚽ Recordatorio CorneApp',
          'No olvides marcar tu disponibilidad para este fin de semana',
          '/disponibilidad'
        );
      }
    } catch (error) {
      // En caso de error, enviar notificación por seguridad
      this.enviarNotificacion(
        '⚽ Recordatorio CorneApp',
        'No olvides marcar tu disponibilidad para este fin de semana',
        '/disponibilidad'
      );
    }
  }

  /**
   * Envía una notificación local
   */
  private enviarNotificacion(titulo: string, mensaje: string, url?: string): void {
    if (!this.hasPermission()) {
      return;
    }
    
    const notification = new Notification(titulo, {
      body: mensaje,
      icon: '/icon-192x192.png',
      badge: '/icon-192x192.png',
      tag: 'disponibilidad-reminder',
      requireInteraction: true
    });

    if (url) {
      notification.onclick = () => {
        window.focus();
        window.location.href = url;
        notification.close();
      };
    }

    // Auto-cerrar después de 10 segundos
    setTimeout(() => {
      notification.close();
    }, 10000);
  }

  /**
   * Guarda el ID del timeout para poder cancelarlo
   */
  private guardarTimeoutId(timeoutId: NodeJS.Timeout): void {
    const timeouts = this.obtenerTimeoutIds();
    timeouts.push(timeoutId as any);
    localStorage.setItem(this.SCHEDULED_NOTIFICATIONS_KEY, JSON.stringify(timeouts));
  }

  /**
   * Obtiene los IDs de timeouts guardados
   */
  private obtenerTimeoutIds(): number[] {
    const stored = localStorage.getItem(this.SCHEDULED_NOTIFICATIONS_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  /**
   * Verifica si ya hay notificaciones programadas para un usuario
   */
  tieneNotificacionesProgramadas(userId: number): boolean {
    const timeoutIds = this.obtenerTimeoutIds();
    const storedUserId = localStorage.getItem(this.NOTIFICATIONS_USER_KEY);
    const isActive = localStorage.getItem(this.NOTIFICATIONS_ACTIVE_KEY) === 'true';
    
    return timeoutIds.length > 0 && 
           storedUserId === userId.toString() && 
           isActive;
  }

  /**
   * Marca las notificaciones como activas para un usuario
   */
  private marcarNotificacionesActivas(userId: number): void {
    localStorage.setItem(this.NOTIFICATIONS_ACTIVE_KEY, 'true');
    localStorage.setItem(this.NOTIFICATIONS_USER_KEY, userId.toString());
  }

  /**
   * Programa notificaciones de disponibilidad
   */
  private async programarNotificacionesDisponibilidad(userId: number): Promise<void> {
    
    if (!this.hasPermission()) {
      const granted = await this.requestPermission();
      if (!granted) {
        return;
      }
    }

    // Cancelar notificaciones anteriores
    this.cancelarNotificacionesProgramadas();

    // Programar para lunes, martes, miércoles y jueves
    const diasSemana = [1, 2, 3, 4]; // 1=Lunes, 2=Martes, 3=Miércoles, 4=Jueves
    const horarios = [
      { hora: 10, minuto: 0 },   // 10:00
      { hora: 15, minuto: 0 },   // 15:00
      { hora: 20, minuto: 30 }   // 20:30
    ];

    let notificacionesProgramadas = 0;
    diasSemana.forEach(dia => {
      horarios.forEach(horario => {
        this.programarNotificacion(userId, dia, horario.hora, horario.minuto);
        notificacionesProgramadas++;
      });
    });
    
    // Marcar como activas
    this.marcarNotificacionesActivas(userId);
  }

  /**
   * Cancela todas las notificaciones programadas
   */
  cancelarNotificacionesProgramadas(): void {
    const timeoutIds = this.obtenerTimeoutIds();
    timeoutIds.forEach(id => clearTimeout(id));
    localStorage.removeItem(this.SCHEDULED_NOTIFICATIONS_KEY);
    localStorage.removeItem(this.NOTIFICATIONS_ACTIVE_KEY);
    localStorage.removeItem(this.NOTIFICATIONS_USER_KEY);
  }

  /**
   * Activa las notificaciones para un usuario (solo si no están ya programadas)
   */
  async activarNotificaciones(userId: number): Promise<boolean> {
    // Verificar si ya están programadas
    if (this.tieneNotificacionesProgramadas(userId)) {
      return true;
    }

    const hasPermission = await this.requestPermission();
    if (hasPermission) {
      await this.programarNotificacionesDisponibilidad(userId);
      return true;
    }
    return false;
  }

  /**
   * Desactiva las notificaciones
   */
  desactivarNotificaciones(): void {
    this.cancelarNotificacionesProgramadas();
  }
}

export const notificationService = new NotificationService();